import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { RouteMap } from './RouteMap';
import { calculateTotalDistance } from '../../utils/distance';
import { formatDuration, formatTimeInBrussels } from '../../utils/format';
import { positionsService } from '../../services/positions';
import { sessionsService } from '../../services/sessions';

interface Position {
  latitude: number;
  longitude: number;
  timestamp: string;
  accuracy?: number;
}

interface Session {
  sessionId: string;
  status: string;
  driverId: string;
  busId: string;
  assignmentId: string;
  startedAt: string;
  updatedAt: string;
  licensePlate?: string;
  transportId?: string;
  journeyId?: string;
}

export function PositionsViewPage() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();

  // Fetch session details
  const { data: sessionData, isLoading: sessionLoading } = useQuery({
    queryKey: ['session', sessionId],
    queryFn: () => sessionId ? sessionsService.getSession(sessionId) : Promise.reject('No session ID'),
    enabled: !!sessionId,
  });

  // Fetch positions for the session
  const { data: positionsData, isLoading: positionsLoading } = useQuery({
    queryKey: ['positions', sessionId],
    queryFn: async () => {
      if (!sessionId) return [];
      const positions = await positionsService.getPositionsBySessionId(sessionId);
      return positions;
    },
    enabled: !!sessionId,
  });

  if (!sessionId) {
    return (
      <div className="p-6 text-center">
        <p className="text-red-600">Session ID manquant</p>
      </div>
    );
  }

  const session = sessionData as Session | undefined;
  const positions = (positionsData as Position[] | undefined) || [];
  const totalDistance = calculateTotalDistance(positions);
  const durationStr = session ? formatDuration(session.startedAt, session.updatedAt) : '';

  const isLoading = sessionLoading || positionsLoading;

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  if (!session) {
    return (
      <div className="p-6 text-center">
        <p className="text-red-600">Session non trouvée</p>
        <button
          onClick={() => navigate('/sessions')}
          className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          Retour aux sessions
        </button>
      </div>
    );
  }

  const numPositions = positions.length;
  const avgAccuracy =
    positions.length > 0
      ? Math.round(
          (positions.reduce((sum, p) => sum + (p.accuracy || 0), 0) / positions.length) * 100
        ) / 100
      : 0;

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Route - Session {sessionId}</h1>
          <p className="text-gray-600 mt-1">Visualisation complète de l'itinéraire</p>
        </div>
        <button
          onClick={() => navigate('/sessions')}
          className="px-4 py-2 text-blue-600 hover:bg-blue-50 rounded-lg border border-blue-600 transition-colors"
        >
          ← Retour
        </button>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
        {/* Session Info */}
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <p className="text-sm text-gray-600 font-medium">Transport</p>
          <p className="text-lg font-semibold text-gray-900">{session.transportId || '-'}</p>
          <p className="text-xs text-gray-500 mt-2">Trajet: {session.journeyId || '-'}</p>
        </div>

        {/* Duration */}
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <p className="text-sm text-gray-600 font-medium">Durée</p>
          <p className="text-lg font-semibold text-gray-900">{durationStr}</p>
          <p className="text-xs text-gray-500 mt-2">
            {formatTimeInBrussels(session.startedAt)}
          </p>
        </div>

        {/* Distance */}
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <p className="text-sm text-gray-600 font-medium">Distance</p>
          <p className="text-lg font-semibold text-gray-900">{totalDistance} km</p>
          <p className="text-xs text-gray-500 mt-2">{numPositions} positions</p>
        </div>

        {/* Driver Info */}
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <p className="text-sm text-gray-600 font-medium">Conducteur</p>
          <p className="text-lg font-semibold text-gray-900">{session.driverId}</p>
          <p className="text-xs text-gray-500 mt-2">ID: {session.assignmentId}</p>
        </div>

        {/* Bus Info */}
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <p className="text-sm text-gray-600 font-medium">Bus</p>
          <p className="text-lg font-semibold text-gray-900">{session.licensePlate || session.busId}</p>
          <p className="text-xs text-gray-500 mt-2">Précision: ±{avgAccuracy}m</p>
        </div>
      </div>

      {/* Map */}
      <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Itinéraire</h2>
        <RouteMap positions={positions} loading={isLoading} />
      </div>

      {/* Detailed Positions Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
          <h2 className="text-lg font-semibold text-gray-900">Positions détaillées ({numPositions})</h2>
        </div>

        {positions.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">#</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Heure</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Latitude</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Longitude</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Précision (m)</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {positions.map((position, index) => (
                  <tr key={index} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3 text-sm text-gray-700">{index + 1}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      {formatTimeInBrussels(position.timestamp)}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700 font-mono">
                      {position.latitude.toFixed(6)}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700 font-mono">
                      {position.longitude.toFixed(6)}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      {position.accuracy ? position.accuracy.toFixed(0) : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="p-6 text-center text-gray-500">
            <p>Aucune position trouvée pour cette session</p>
          </div>
        )}
      </div>
    </div>
  );
}
